"use client";

import Link from "next/link";
import type { CShopOrderContent } from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { useFrontendSession } from "@/lib/api/session-context";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { cShopTranslations } from "./CShop.i18n";
import styles from "./CShop.module.css";
import {
  CartSummary,
  OrderFormLogged,
  OrderSteps,
  OrderTabsGuest,
} from "./CShop";

/* ─── Krok 2 - formularz zamowienia (`c_shop` view=order) ─────────────── */

/**
 * CShopOrder - widok kroku 2 flow zamawiania (formularz danych + faktura).
 *
 * Renderuje:
 *   - wskaznik krokow (aktywny krok 2)
 *   - lewy panel: formularz danych (1 tryb dla zalogowanego, 2 tabs dla guesta
 *     + button "Zaloguj sie" otwierajacy modal WUserAccount)
 *   - prawy panel: sticky podsumowanie z kroku 1
 *
 * **Tryby dla niezalogowanego (2 tabs):**
 *   - `new_client` (type=1) - rejestracja + zamowienie (login+haslo)
 *   - `just_order` (type=2) - zamowienie bez rejestracji
 *
 * Tab "Nowy klient" pokazywany tylko gdy `is_registration_enabled`.
 */
export function CShopOrder({ content }: { content: CShopOrderContent }) {
  const t = useT(cShopTranslations);
  const session = useFrontendSession();

  const listHref = extractInternalHref(content.href.list);
  const currency = content.currency ?? "";

  /* Server-rendered `is_logged` bierze pod uwage sesje z REST (Bearer token
     z apiClient). Client-side `session` czyta cookie `imperial_frontend_session`.
     Rozbieznosc mozliwa tylko przez rownolegle logowanie w innej karcie -
     po router.refresh() oba znowu sie zgadzaja. Trybu logged uzywamy gdy
     KTOROKOLWIEK jest zalogowane (bezpieczna intersekcja UX). */
  const isLogged = content.is_logged || session !== null;

  //Empty state - pusty koszyk / brak card_id
  if (content.products.length === 0 || content.card_id === null) {
    return (
      <div className={styles.root}>
        <OrderSteps steps={content.steps} />
        <div className={styles.empty}>{t.order.empty}</div>
        {listHref !== null && (
          <div className={styles.actions}>
            <div className={styles.actionsLeft}>
              <Link href={listHref} className={styles.buttonSecondary}>
                {t.cart.continueShopping}
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <OrderSteps steps={content.steps} />

      <div className={styles.orderGrid}>
        <div className={styles.orderMain}>
          {isLogged ? (
            <OrderFormLogged content={content} t={t} />
          ) : (
            <OrderTabsGuest content={content} t={t} />
          )}
        </div>

        <div className={styles.orderSummary}>
          <CartSummary total={content.total} currency={currency} t={t} />
        </div>
      </div>
    </div>
  );
}
